import TextBox from "basket/bascketBody/TextBox";
import { useEffect, useRef, useState } from "react";

export default function ProductQtyInput({
    initValue = 1,
    onlyInt = true,
    showButtons = true,
    digits = 1,
    changeStep = 1,
    label = '',
    onChange = null,
    onBlur = null
}) {

    const [qty, setQty] = useState(initValue > 0 ? String(initValue) : '');

    // Last known valid value, equivalent to the module-level prevQtyValue in
    // validationAsNumber() (wwwroot/js/scripts.js) that the plain #productQty
    // input on the fabric/wallpaper product pages reverts to on bad input.
    const prevValue = useRef(initValue > 0 ? String(initValue) : '1');

    const normalize = (value) => {
        const number = Number(value);

        if (isNaN(number)) return 1;
        if (number < 0) return 1;

        if (onlyInt) {
            return Math.trunc(number);
        }

        return Number(number.toFixed(digits));
    };

    // A React state update alone does not always force the native <input> to
    // resync (React bails out when the new state is identical to the old
    // state), so unlike scripts.js's $inp.val(...), setQty() alone can leave
    // stray characters visible in the box even though they were rejected.
    // Writing e.target.value directly, the same imperative approach
    // validationAsNumber() uses, guarantees the box always shows the
    // sanitised value.
    const setValue = (e, value) => {
        e.target.value = value;
        setQty(value);
    };

    // Mirrors validationAsNumber() in wwwroot/js/scripts.js, which is the
    // same validation the plain #productQty input uses on the fabric and
    // wallpaper product pages: digits and at most one decimal point, rounded
    // live to `digits` decimal places, reverting to the last valid value on
    // anything else.
    const handleChanges = (e) => {
        let value = e.target.value;
        value = value.replace(',', '.');

        if (onlyInt) {
            value = value.replace(/\D/g, '');
        }

        if (value === '') {
            setValue(e, '');
            onChange && onChange(0);
            return;
        }

        if (!/^\d*\.?\d*$/.test(value)) {
            setValue(e, prevValue.current);
            return;
        }

        if (value === '0') {
            prevValue.current = '1';
            setValue(e, '1');
            onChange && onChange(1);
            return;
        }

        if (value.endsWith('.')) {
            setValue(e, value);
            return;
        }

        const factor = Math.pow(10, digits);
        let rounded = Math.round(parseFloat(value) * factor) / factor;
        if (rounded <= 0) rounded = 1;

        prevValue.current = String(rounded);
        setValue(e, String(rounded));
        onChange && onChange(rounded);
    };

    const handleBlur = () => {
        const normalized = normalize(qty);

        if (!qty || normalized <= 0) {
            prevValue.current = '1';
            setQty('1');
            onChange && onChange(1);
            onBlur && onBlur();
            return;
        }

        prevValue.current = String(normalized);
        setQty(String(normalized));
        onChange && onChange(normalized);
        onBlur && onBlur();
    };

    const handleMinus = () => {
        const current = qty === '' ? 1 : Number(qty);
        const next = Math.max(1, current - changeStep).toFixed(digits);

        prevValue.current = next;
        setQty(next);
        onChange && onChange(next);
    };

    const handlePlus = () => {
        const current = qty === '' ? 1 : Number(qty);
        const next = (current + changeStep).toFixed(digits);

        prevValue.current = next;
        setQty(next);
        onChange && onChange(next);
    };

    useEffect(() => {
        const next = initValue > 0 ? String(initValue) : '';
        prevValue.current = initValue > 0 ? next : '1';
        setQty(next);
    }, [initValue]);

    return (
        <div className="d-flex gap-24">

            {showButtons && (
                <div className="round-border-btn minus-btn pointer" onClick={handleMinus}>
                    <span className="curtains-icon curtains-icon-minus"></span>
                </div>
            )}

            <div className="body-text5 align-center gap_2">
                <TextBox
                    className="max-width-100"
                    name={'productQty'}
                    value={qty}
                    onChange={handleChanges}
                    onBlur={handleBlur}
                />
                {label}
            </div>

            {showButtons && (
                <div className="round-border-btn plus-btn pointer" onClick={handlePlus}>
                    <span className="curtains-icon curtains-icon-plus"></span>
                </div>
            )}
        </div>
    );
}